<template>
  <div>
    <div class="list-toolbar">
      <div class="toolbar-top">
        <div>
          <h2 class="list-title">My Submissions</h2>
          <p class="list-subtitle">
            View your submitted timesheets for any date range
          </p>
        </div>
        <div class="toolbar-actions">
          <b-button variant="secondary" size="sm" @click="loadHistory">
            <b-icon-arrow-clockwise /> Refresh
          </b-button>
          <b-dropdown size="sm" :disabled="exporting" variant="primary">
            <template #button-content> <b-icon-download /> Export </template>
            <b-dropdown-item @click="exportHistory('csv')">
              Export as CSV
            </b-dropdown-item>
            <b-dropdown-item @click="exportHistory('pdf')">
              Export as PDF
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <div class="toolbar-row">
        <div class="filter-field filter-field-date">
          <label class="filter-label">From</label>
          <b-form-datepicker
            v-model="dateFrom"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
            class="date-input"
            @input="onDateFromChange"
          />
        </div>

        <div class="filter-field filter-field-date">
          <label class="filter-label">To</label>
          <b-form-datepicker
            v-model="dateTo"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
            class="date-input"
          />
        </div>

        <div class="filter-field">
          <label class="filter-label">Project</label>
          <b-form-select
            v-model="selectedProjectId"
            :options="projectOptions"
            size="sm"
            class="filter-control filter-control-project"
          />
        </div>

        <div class="filter-field">
          <label class="filter-label">Week</label>
          <div class="week-nav">
            <b-button
              variant="secondary"
              size="sm"
              class="week-nav-btn"
              aria-label="Current week"
              title="Current week"
              @click="applyCurrentWeek"
            >
              <b-icon-calendar3 />
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="week-nav-btn"
              aria-label="Previous week"
              @click="shiftWeek(-1)"
            >
              <b-icon-chevron-left />
            </b-button>
            <span class="week-nav-label">{{ weekLabel }}</span>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="week-nav-btn"
              aria-label="Next week"
              @click="shiftWeek(1)"
            >
              <b-icon-chevron-right />
            </b-button>
          </div>
        </div>

        <div class="filter-field">
          <label class="filter-label">Search</label>
          <b-form-input
            v-model="searchQuery"
            placeholder="Tradie name…"
            size="sm"
            class="filter-control filter-control-search"
          />
        </div>

        <div class="filter-field filter-field-action">
          <label class="filter-label">Filters</label>
          <b-dropdown
            :variant="activeFilterCount ? 'primary' : 'outline-secondary'"
            size="sm"
            right
            menu-class="timesheet-filter-menu"
            class="filter-dropdown"
          >
            <template #button-content>
              <b-icon-funnel /> Filter
              <span v-if="activeFilterCount" class="filter-count">{{
                activeFilterCount
              }}</span>
            </template>
            <b-dropdown-form class="filter-dropdown-form" @click.stop>
              <p class="filter-menu-label">Status</p>
              <b-form-checkbox-group
                v-model="statusFilter"
                :options="statusFilterOptions"
                stacked
              />
            </b-dropdown-form>
          </b-dropdown>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <div
      v-else-if="filteredHistory.length === 0"
      class="text-center py-5 text-muted"
    >
      <p v-if="history.length === 0">No submitted timesheets yet.</p>
      <p v-else>No submissions match your filters.</p>
    </div>

    <b-card
      v-for="ts in filteredHistory"
      :key="ts.id"
      :data-timesheet-id="ts.id"
      no-body
      class="mb-3"
    >
      <b-card-header
        class="d-flex align-items-center justify-content-between py-2 px-3 submission-header"
        style="cursor: pointer"
        @click="toggle(ts.id)"
      >
        <div class="submission-header-title">
          <span class="font-weight-bold mr-2">{{ ts.project_name }}</span>
          <span class="text-muted small">{{ formatWeek(ts.week_start) }}</span>
        </div>
        <div class="d-flex align-items-center submission-header-actions">
          <b-badge
            :variant="statusVariant(ts.status)"
            class="mr-2 text-capitalize"
          >
            {{ ts.status }}
          </b-badge>
          <b-button
            v-if="ts.status === 'rejected' || ts.status === 'submitted'"
            :variant="amendVariant(ts.status)"
            size="sm"
            class="mr-2 amend-btn"
            @click.stop="$emit('amend', ts)"
          >
            {{ amendLabel(ts.status) }}
          </b-button>
          <b-button
            v-if="ts.status === 'draft'"
            variant="primary"
            size="sm"
            class="mr-2 amend-btn"
            @click.stop="$emit('amend', ts)"
          >
            Edit Draft
          </b-button>
          <b-button
            v-if="ts.status === 'draft'"
            variant="outline-danger"
            size="sm"
            class="mr-2 amend-btn"
            :disabled="deletingId === ts.id"
            @click.stop="deleteDraft(ts)"
          >
            Delete
          </b-button>
          <b-icon
            :icon="expandedId === ts.id ? 'chevron-up' : 'chevron-down'"
          />
        </div>
      </b-card-header>

      <b-collapse :visible="expandedId === ts.id">
        <b-card-body class="px-3 pt-2 pb-3">
          <!-- Rejection reason -->
          <b-alert
            v-if="ts.status === 'rejected'"
            variant="danger"
            show
            class="small"
          >
            <strong>{{ ts.rejection_reason }}</strong>
            <span v-if="ts.rejection_comment">
              - {{ ts.rejection_comment }}</span
            >
          </b-alert>

          <!-- Supervisor comment -->
          <p v-if="ts.supervisor_comment" class="small text-muted mb-2">
            <strong>Your note:</strong> {{ ts.supervisor_comment }}
          </p>

          <!-- Submission photos -->
          <div class="mb-3 p-3 soft-panel small">
            <strong>Photos:</strong>
            <div
              v-if="ts.photo_urls && ts.photo_urls.length"
              class="event-photos mt-2"
            >
              <div
                v-for="(photo, i) in ts.photo_urls"
                :key="photo.id"
                style="display: inline-block; cursor: pointer"
                @click.stop="openLightbox(ts.photo_urls, i)"
              >
                <img
                  :src="photo.url"
                  class="event-photo-thumb"
                  :alt="'Photo ' + (i + 1)"
                />
              </div>
            </div>
            <span v-else class="text-muted"> No photos attached</span>
          </div>

          <!-- Worker summary -->
          <div class="table-responsive mb-3">
            <table
              class="table table-sm table-bordered mb-0 worker-summary-table"
              style="font-size: 0.78rem"
            >
              <thead class="thead-light">
                <tr>
                  <th>Worker</th>
                  <th
                    v-for="col in weekCols(ts.week_start)"
                    :key="col.date"
                    class="text-center"
                  >
                    {{ col.label }}<br /><span class="text-muted">{{
                      col.dateStr
                    }}</span>
                  </th>
                  <th class="text-center">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in ts.rows"
                  :key="row.worker.id"
                  :class="{ 'table-secondary': !row.on_site }"
                >
                  <td>
                    <div class="font-weight-medium">
                      {{ row.worker.full_name }}
                    </div>
                    <small v-if="row.worker.trade" class="text-muted">{{
                      formatTrade(row.worker.trade)
                    }}</small>
                  </td>
                  <td
                    v-for="(day, i) in row.days"
                    :key="day.date"
                    class="text-center align-middle small"
                    :data-label="dayLabel(ts.week_start, i)"
                  >
                    <template v-if="day.present && day.start && day.finish">
                      {{ day.start }}–{{ day.finish }}
                      <div
                        v-if="day.break_minutes"
                        class="text-muted"
                        style="font-size: 10px"
                      >
                        {{ day.break_minutes }}m brk
                      </div>
                    </template>
                    <b-badge v-else-if="day.leave_type" variant="info">
                      {{ leaveTypeLabel(day.leave_type) }}
                    </b-badge>
                    <span v-else class="text-muted">-</span>
                    <div
                      v-if="day.plant_entries && day.plant_entries.length"
                      class="readonly-plant-list"
                    >
                      <div
                        v-for="pe in day.plant_entries"
                        :key="pe.id"
                        class="readonly-plant-line"
                      >
                        {{ pe.plant_name }} · {{ pe.quantity }}
                        {{ plantUnitLabel(pe.rate_type) }}
                      </div>
                    </div>
                  </td>
                  <td
                    class="text-center align-middle font-weight-bold"
                    data-label="Total"
                  >
                    {{ rowTotal(row) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Crew plant & equipment (sheet-level). Supervisors never see
               dollar rates/claims — that's business-admin-only, same rule
               SupervisorSubmitForm.vue follows — so show-cost is false. -->
          <template v-if="ts.plant_entries && ts.plant_entries.length">
            <p
              class="small font-weight-bold text-muted mb-1 text-uppercase"
              style="letter-spacing: 0.05em"
            >
              Plant &amp; Equipment
            </p>

            <supervisor-crew-plant-panel
              :entries="ts.plant_entries"
              :show-cost="false"
            />
            <supervisor-crew-plant-cards
              :entries="ts.plant_entries"
              :show-cost="false"
            />
          </template>

          <!-- Audit trail -->
          <supervisor-audit-trail
            v-if="ts.audit && ts.audit.length"
            :audit="ts.audit"
          />
        </b-card-body>
      </b-collapse>
    </b-card>

    <!-- Photo lightbox -->
    <b-modal
      id="supervisor-history-photo-lightbox-modal"
      size="xl"
      centered
      hide-header
      :hide-footer="lightboxPhotos.length <= 1"
      body-class="p-1 text-center bg-dark"
      footer-class="justify-content-center"
      @hidden="
        lightboxPhotos = []
        lightboxIndex = 0
      "
    >
      <img
        v-if="lightboxPhotos.length"
        :src="lightboxPhotos[lightboxIndex]"
        style="max-width: 100%; max-height: 80vh; object-fit: contain"
      />
      <template #modal-footer>
        <b-button variant="light" @click="lightboxPrev">&#8592; Prev</b-button>
        <span class="mx-3 text-white"
          >{{ lightboxIndex + 1 }} / {{ lightboxPhotos.length }}</span
        >
        <b-button variant="light" @click="lightboxNext">Next &#8594;</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  formatTrade,
  dayMinutes as minutesWorked,
  formatHours,
  leaveTypeLabel,
  plantUnitLabel,
} from '@/utils/timesheetHelpers'
import {
  startDayOf,
  settingsStartDay,
  weekDates,
  formatDayHeader,
} from '@/utils/weekHelpers'
import SupervisorCrewPlantPanel from '@/components/timesheets/supervisor/SupervisorCrewPlantPanel.vue'
import SupervisorCrewPlantCards from '@/components/timesheets/supervisor/SupervisorCrewPlantCards.vue'
import SupervisorAuditTrail from '@/components/timesheets/supervisor/SupervisorAuditTrail.vue'

export default {
  name: 'SupervisorSubmitHistory',

  components: {
    SupervisorCrewPlantPanel,
    SupervisorCrewPlantCards,
    SupervisorAuditTrail,
  },

  data() {
    return {
      history: [],
      loading: false,
      exporting: false,
      deletingId: null,
      expandedId: null,
      dateFrom: '',
      dateTo: '',
      /** en-AU default renders "28 July 2026"; force numeric dd/mm/yyyy. */
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
      selectedProjectId: null,
      searchQuery: '',
      statusFilter: ['draft', 'submitted', 'approved', 'rejected'],
      statusFilterOptions: [
        { value: 'draft', text: 'Draft' },
        { value: 'submitted', text: 'Pending' },
        { value: 'approved', text: 'Approved' },
        { value: 'rejected', text: 'Rejected' },
      ],
      lightboxPhotos: [],
      lightboxIndex: 0,
    }
  },

  computed: {
    weekLabel() {
      if (!this.dateFrom || !this.dateTo) return '-'
      const from = new Date(this.dateFrom + 'T00:00:00')
      const to = new Date(this.dateTo + 'T00:00:00')
      const month = (d) => d.toLocaleDateString('en-AU', { month: 'short' })
      // Non-breaking space inside each date so the label can only ever wrap
      // around the dash, never mid-date.
      const nbsp = '\u00A0'
      if (
        from.getMonth() === to.getMonth() &&
        from.getFullYear() === to.getFullYear()
      ) {
        // Same month: compact "13–19 Jul" fits phone-width filter columns.
        return `${from.getDate()}–${to.getDate()}${nbsp}${month(to)}`
      }
      return `${from.getDate()}${nbsp}${month(
        from,
      )} – ${to.getDate()}${nbsp}${month(to)}`
    },

    activeFilterCount() {
      const n = this.statusFilter.length
      return n === 0 || n === this.statusFilterOptions.length ? 0 : n
    },

    projectOptions() {
      const seen = {}
      this.history.forEach((ts) => {
        if (ts.project_id != null) seen[ts.project_id] = ts.project_name
      })
      return [{ value: null, text: 'All Projects' }].concat(
        Object.keys(seen).map((id) => ({
          value: Number(id),
          text: seen[id],
        })),
      )
    },

    filteredHistory() {
      const q = this.searchQuery.trim().toLowerCase()
      const from = this.dateFrom ? new Date(this.dateFrom + 'T00:00:00') : null
      const to = this.dateTo ? new Date(this.dateTo + 'T23:59:59') : null

      return this.history.filter((ts) => {
        if (
          this.selectedProjectId != null &&
          ts.project_id !== this.selectedProjectId
        )
          return false

        if (this.statusFilter.length && !this.statusFilter.includes(ts.status))
          return false

        if (from || to) {
          const weekStart = new Date(ts.week_start + 'T00:00:00')
          const weekEnd = new Date(weekStart)
          weekEnd.setDate(weekEnd.getDate() + 6)
          if (from && weekEnd < from) return false
          if (to && weekStart > to) return false
        }

        if (q) {
          const matchesProject = ts.project_name.toLowerCase().includes(q)
          const matchesWorker = (ts.rows || []).some((row) =>
            row.worker.full_name.toLowerCase().includes(q),
          )
          if (!matchesProject && !matchesWorker) return false
        }

        return true
      })
    },
  },

  async mounted() {
    // Cached after first load — cheap. Must resolve before applyCurrentWeek()
    // so the default week honours the business's configured start day.
    await this.$store.dispatch('timesheet/getTimesheetSettings')
    this.applyCurrentWeek()
    await this.loadHistory()
    this._autoExpandFromQuery()
  },

  methods: {
    plantUnitLabel,
    _autoExpandFromQuery() {
      const id = parseInt(this.$route.query.timesheetId, 10)
      if (!id) return
      const sheet = this.history.find((s) => s.id === id)
      if (!sheet) return
      // Ensure the sheet isn't hidden by the status filter
      if (!this.statusFilter.includes(sheet.status)) {
        this.statusFilter = [...this.statusFilter, sheet.status]
      }
      // Ensure the sheet's week isn't hidden by the default current-week
      // date range — notifications can point at a timesheet from a
      // different week than the one currently displayed.
      if (sheet.week_start) {
        const weekEnd = weekDates(sheet.week_start)[6]
        if (!this.dateFrom || sheet.week_start < this.dateFrom) {
          this.dateFrom = sheet.week_start
        }
        if (!this.dateTo || weekEnd > this.dateTo) {
          this.dateTo = weekEnd
        }
      }
      this.expandedId = id
      this.$nextTick(() => {
        const el = document.querySelector(`[data-timesheet-id="${id}"]`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },

    formatTrade,
    leaveTypeLabel,

    applyCurrentWeek() {
      const settings = this.$store.getters['timesheet/timesheetSettings']
      const weekStart = startDayOf(null, settingsStartDay(settings))
      this.dateFrom = weekStart
      this.dateTo = weekDates(weekStart)[6]
    },

    onDateFromChange() {
      if (!this.dateFrom) return
      const from = new Date(this.dateFrom + 'T00:00:00')
      const to = new Date(from)
      to.setDate(from.getDate() + 6)
      this.dateTo = to.toISOString().slice(0, 10)
    },

    shiftWeek(direction) {
      const from = new Date((this.dateFrom || this.dateTo) + 'T00:00:00')
      const to = new Date((this.dateTo || this.dateFrom) + 'T00:00:00')
      from.setDate(from.getDate() + direction * 7)
      to.setDate(to.getDate() + direction * 7)
      const fmt = (d) => d.toISOString().slice(0, 10)
      this.dateFrom = fmt(from)
      this.dateTo = fmt(to)
    },

    async loadHistory() {
      this.loading = true
      try {
        this.history = await this.$store.dispatch(
          'timesheet/supervisorFetchHistory',
        )
      } catch {
        this.$bvToast.toast('Failed to load history.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.loading = false
      }
    },

    async exportHistory(format) {
      const ids = this.filteredHistory.map((t) => t.id)
      if (!ids.length) {
        this.$bvToast.toast('No submissions to export', {
          variant: 'warning',
          title: 'Export',
        })
        return
      }
      if (this.exporting) return
      this.exporting = true
      try {
        const res = await this.$store.dispatch(
          'timesheet/supervisorExportHistory',
          { ids, format },
        )
        const blob = res.data
        const now = new Date()
        const dd = String(now.getDate()).padStart(2, '0')
        const mm = String(now.getMonth() + 1).padStart(2, '0')
        let filename = `supervisor-timesheets-${dd}-${mm}-${now.getFullYear()}.${format}`
        const disposition = res.headers && res.headers['content-disposition']
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/)
          if (match && match[1]) filename = match[1]
        }
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$bvToast.toast('Failed to export submissions', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.exporting = false
      }
    },

    toggle(id) {
      this.expandedId = this.expandedId === id ? null : id
    },

    async deleteDraft(ts) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Delete the draft timesheet for "${
          ts.project_name || 'this project'
        }" (${this.formatWeek(ts.week_start)})? This can't be undone.`,
        {
          title: 'Delete Draft Timesheet',
          okTitle: 'Delete',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!ok) return

      this.deletingId = ts.id
      try {
        await this.$store.dispatch('timesheet/supervisorDeleteTimesheet', ts.id)
        this.history = this.history.filter((s) => s.id !== ts.id)
        this.$bvToast.toast('Draft timesheet deleted.', {
          variant: 'success',
          title: 'Deleted',
        })
      } catch (e) {
        this.$bvToast.toast('Failed to delete draft timesheet.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.deletingId = null
      }
    },

    statusVariant(s) {
      return (
        {
          submitted: 'warning',
          approved: 'success',
          rejected: 'danger',
          draft: 'secondary',
        }[s] || 'secondary'
      )
    },

    amendLabel(status) {
      return status === 'submitted' ? 'Edit & Resubmit' : 'Amend & Resubmit'
    },

    amendVariant(status) {
      return status === 'submitted' ? 'outline-warning' : 'warning'
    },

    weekCols(weekStart) {
      return weekDates(weekStart).map((iso) => {
        const { weekday, dayLabel } = formatDayHeader(iso)
        return { label: weekday, date: iso, dateStr: dayLabel }
      })
    },

    dayLabel(weekStart, i) {
      const col = this.weekCols(weekStart)[i]
      return `${col.label} ${col.dateStr}`
    },

    rowTotal(row) {
      const mins = row.days.reduce((sum, d) => sum + minutesWorked(d), 0)
      return mins > 0 ? formatHours(mins) : '-'
    },

    formatWeek(weekStart) {
      const base = new Date(weekStart + 'T00:00:00')
      const end = new Date(base)
      end.setDate(base.getDate() + 6)
      const fmt = (d) =>
        d.toLocaleDateString('en-AU', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      return `${fmt(base)} – ${fmt(end)}`
    },

    openLightbox(photos, index) {
      this.lightboxPhotos = photos.map((p) => p.url)
      this.lightboxIndex = index
      this.$bvModal.show('supervisor-history-photo-lightbox-modal')
    },
    lightboxPrev() {
      this.lightboxIndex =
        (this.lightboxIndex - 1 + this.lightboxPhotos.length) %
        this.lightboxPhotos.length
    },
    lightboxNext() {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.lightboxPhotos.length
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';
@import '~@/assets/scss/layouts/supervisor-worker-shared';

.list-toolbar {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 20px;
  margin: -1rem -1rem 1rem;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.submission-header {
  flex-wrap: wrap;
  row-gap: 8px;
}

.submission-header-title {
  min-width: 0;
  overflow-wrap: break-word;
}

.submission-header-actions {
  flex-wrap: wrap;
  row-gap: 6px;
  max-width: 100%;
}

.amend-btn {
  white-space: normal;
  flex-shrink: 1;
  min-width: 0;
}

.toolbar-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.list-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}

.list-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.toolbar-row {
  display: grid;
  grid-template-columns:
    minmax(132px, 0.75fr) minmax(132px, 0.75fr) minmax(180px, 1.1fr)
    max-content minmax(180px, 1fr) auto;
  align-items: flex-end;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.filter-field-action {
  white-space: nowrap;
}

.filter-field-date {
  min-width: 132px;
}

.date-input,
.filter-control {
  width: 100%;
}

/* b-form-datepicker's own .b-form-btn-label-control.form-control rule
   (two classes) beats the app-wide `.form-control { height: 50px }` rule
   (one class) on specificity, so it renders shorter than the other sm-sized
   filter controls here despite sharing size="sm". Match it back to 50px. */
/deep/ .date-input.form-control {
  height: 50px;
}

.week-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}

.week-nav-btn {
  width: 32px;
  padding-left: 0;
  padding-right: 0;
  flex-shrink: 0;
}

.week-nav-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex: 1 0 auto;
  text-align: center;
  line-height: 1.2;
  /* Fixed-width buttons either side always leave enough room for the short
     date range, so keep it on one line rather than wrapping at the dash. */
  white-space: nowrap;

  @media (max-width: 360px) {
    font-size: 12px;
  }
}

@media (max-width: 360px) {
  .week-nav {
    gap: 4px;
  }

  .week-nav-btn {
    width: 28px;
  }
}

.filter-dropdown {
  width: 100%;
}

.filter-dropdown ::v-deep > .btn {
  min-width: 112px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 1;
  background: rgba(255, 255, 255, 0.24);
  color: inherit;
}

::v-deep .timesheet-filter-menu {
  min-width: 220px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.16);
}

.filter-dropdown-form {
  padding: 4px 6px;
}

.filter-menu-label {
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}

/* Submission photo panel + thumbnails — the markup/lightbox reuse
   SupervisorTimesheetTab's pattern, but its .soft-panel / .event-photo*
   styles are scoped there, so redeclare them. */
.soft-panel {
  margin-bottom: 1rem;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  color: #374151;
}

.event-photos {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.event-photo-thumb {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.8;
  }
}

/* Worker summary table — below 992px (matches the Crew History tab's own
   breakpoint) each row renders as a compact label/value card instead of a
   horizontally scrolling table, so all 7 days + Total are visible without
   scrolling even at 320px. From 992px up it's the normal table. */
@media (max-width: 991.98px) {
  .worker-summary-table {
    display: block;
    border: 0;
  }

  .worker-summary-table thead {
    display: none;
  }

  .worker-summary-table tbody,
  .worker-summary-table tr {
    display: block;
    width: 100%;
  }

  .worker-summary-table tr {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 10px 12px;

    & + tr {
      margin-top: 10px;
    }
  }

  .worker-summary-table td {
    display: grid;
    grid-template-columns: 90px minmax(0, 1fr);
    align-items: baseline;
    gap: 10px;
    padding: 3px 0;
    border: 0;
    text-align: left !important;

    &::before {
      content: attr(data-label);
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #9ca3af;
    }
  }

  /* Worker cell is the card title: full width, no label. */
  .worker-summary-table td:first-child {
    display: block;
    padding-bottom: 6px;
    margin-bottom: 4px;
    border-bottom: 1px solid #f3f4f6;

    &::before {
      display: none;
    }
  }
}
</style>
